import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { InternalAuditReportRiskAssessmentResultsByRiskIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetInternalAuditReportRiskAssessmentResultsByRiskIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC assessment result data to match the GraphQL query structure
 */
const mapTrpcAssessmentResultToGraphQL = (
  trpcData: InternalAuditReportRiskAssessmentResultsByRiskIdResponse
): GetInternalAuditReportRiskAssessmentResultsByRiskIdQuery => {
  return trpcData;
};

export const useGetInternalAuditReportRiskAssessmentResultsByRiskId = (
  riskId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.assessment.internalAuditReportRiskAssessmentResultsByRiskId.queryOptions(
      {
        riskId,
      }
    ),
    enabled: trpcEnabled && !skip && !!riskId,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcError, addNotification, trpcEnabled]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcAssessmentResultToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
